/**
 * useIsModalOpen — React hook that returns ``true`` whenever any
 * full-screen modal is currently mounted in the DOM.
 *
 * iter150AZ.52 — global floating FABs (AssistantFab, HelpDrawer,
 * FloatingLanguagePicker, etc.) used to render on top of, or behind,
 * every modal because they had no awareness of modal state. The
 * resulting visual was multiple "dialog circles" competing for the
 * user's attention — most visibly inside the PM Schedule modal.
 *
 * Detection
 * ---------
 * We watch the document body for ``[role="dialog"]`` AND for the
 * conventional ``fixed inset-0`` modal-backdrop pattern used by the
 * app's own Tailwind modals. A MutationObserver pings whenever the
 * subtree changes so the hook re-evaluates without polling.
 */
import { useEffect, useState } from "react";

const MODAL_SELECTOR = [
  '[role="dialog"]',
  '[data-testid$="-modal"]',
  '[data-testid$="-overlay"]',
  // PM viewer, CompressorDetail, etc — they use this exact class
  // stack as a backdrop wrapper.
  'div.fixed.inset-0.z-50',
  'div.fixed.inset-0.z-\\[60\\]',
  'div.fixed.inset-0.z-\\[70\\]',
  'div.fixed.inset-0.z-\\[1000\\]',
].join(", ");

function _anyModalPresent() {
  if (typeof document === "undefined") return false;
  try {
    return !!document.querySelector(MODAL_SELECTOR);
  } catch {
    return false;
  }
}

export default function useIsModalOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const recompute = () => setOpen(_anyModalPresent());
    recompute();
    const obs = new MutationObserver(() => {
      // Debounce — modal mounts/unmounts can fire bursts of mutations.
      window.requestAnimationFrame(recompute);
    });
    obs.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "role", "data-testid"],
    });
    return () => obs.disconnect();
  }, []);
  return open;
}
